import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, openAuthedFile } from "@/api/client";
import { enumLabel } from "@/i18n/enumLabels";
import {
  INVOICE_TYPE_LABELS,
  INVOICE_STATUS_LABELS,
  type InvoiceType,
  type InvoiceStatus,
  INVOICE_TYPES,
  INVOICE_STATUSES,
} from "@ckgo2/shared/types";
import { formatPrice } from "@ckgo2/shared/utils";
import { formatDate } from "@/lib/format";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import { FileText, Download, Loader2 } from "lucide-react";

interface InvoiceRow {
  id: string;
  number: string;
  orderId?: string | null;
  type: InvoiceType;
  status: InvoiceStatus;
  issueDate: string;
  dueDate?: string | null;
  currency: string;
  total: number;
  orderNumber?: string | null;
  customerFirstName?: string | null;
  customerLastName?: string | null;
}

interface FacetedInvoices {
  data: InvoiceRow[];
  total: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  success: true;
}

const STATUS_BADGE: Record<InvoiceStatus, string> = {
  issued: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-gray-100 text-gray-500",
};

export default function InvoicesPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["invoices", qs],
    queryFn: () => api.get<FacetedInvoices>(`/api/admin/invoices?${qs}`),
  });

  const rows = data?.data ?? [];
  const facets = data?.facets ?? {};

  const openPdf = async (id: string) => {
    setPdfBusy(id);
    try {
      await openAuthedFile(`/api/admin/invoices/${id}/pdf`);
    } finally {
      setPdfBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t("finance.invoices.title")}
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">{t("finance.invoices.subtitle")}</p>
      </div>

      <FacetedListLayout
        searchPlaceholder={t("finance.invoices.searchPlaceholder")}
        total={data?.total ?? 0}
        limit={data?.limit ?? 25}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title={t("common.fields.type")}
              paramKey="type"
              order={{ enum: INVOICE_TYPES }}
              buckets={facets.type ?? []}
              labelFor={(v) => enumLabel(INVOICE_TYPE_LABELS, v as InvoiceType)}
            />
            <FacetPanel
              title={t("common.fields.status")}
              paramKey="status"
              order={{ enum: INVOICE_STATUSES }}
              buckets={facets.status ?? []}
              labelFor={(v) => enumLabel(INVOICE_STATUS_LABELS, v as InvoiceStatus)}
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {rows.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-gray-500">
              <FileText className="mb-2 h-10 w-10 text-gray-300" />
              <p>{t("finance.invoices.empty")}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {[
                    t("finance.invoices.number"),
                    t("finance.invoices.order"),
                    t("common.fields.customer"),
                    t("common.fields.type"),
                    t("common.fields.total"),
                    t("common.fields.status"),
                    t("finance.invoices.due"),
                    "",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 font-mono text-sm text-gray-900 dark:text-white">
                      {r.number}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {r.orderId ? (
                        <Link
                          to={`/orders/${r.orderId}`}
                          className="text-primary-600 dark:text-primary-400 font-mono hover:underline"
                        >
                          {r.orderNumber ?? "—"}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {[r.customerFirstName, r.customerLastName].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {enumLabel(INVOICE_TYPE_LABELS, r.type)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {formatPrice(r.total, r.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[r.status]}`}
                      >
                        {enumLabel(INVOICE_STATUS_LABELS, r.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {r.dueDate ? formatDate(r.dueDate) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openPdf(r.id)}
                        className="btn-ghost btn-sm"
                        disabled={pdfBusy === r.id}
                        title={t("finance.invoices.openPdf")}
                      >
                        {pdfBusy === r.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
